import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/GetApp";
import UploadIcon from "@mui/icons-material/Publish";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import { Button, Grid, styled, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { IFilterOption, ISavedFilter, IUserAppSettings, SearchDataType } from "@vmosolutions/oc-interfaces";
import { cloneDeep, isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { v1 } from "uuid";

interface ISavedSearchFiltersProps {
  dataType: SearchDataType;
  searchFilters: IFilterOption[];
  parentElement: any;
  setAppliedSavedFilter: React.Dispatch<React.SetStateAction<ISavedFilter>>;
}

const OCSavedSearchFilters = styled(Accordion)(({ theme }) => ({
  width: "calc(100% - 20px)",
  margin: "0px 10px 10px 10px",
  "& .MuiAccordionSummary-root": {
    minHeight: 24,
    padding: "5px 15px",
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "& .form_subtitle": {
        margin: "0px 10px",
        color: theme.custom.forms.labels,
      },
    },
    "& .MuiIconButton-root": { padding: 5 },
  },
  "&.Mui-expanded": {
    margin: "0px 10px 10px 10px",
    "&.MuiAccordionSummary-content": { margin: 5 },
  },
  "& .saved_search_selection": {
    marginLeft: 10,
    width: "auto",
    "& .saved_search_icon": {
      position: "relative",
      width: 24,
      height: 24,
      "& .search_icon": {},
      "& .star_icon": {
        width: 9,
        height: 10,
        top: 4,
        left: 5,
        position: "absolute",
      },
    },
    "& .timeline_event_saved_search_filter": {
      width: 263,
    },
  },
  "& .actions": {
    width: "auto",
    alignItems: "center",
    "& .button": {
      fontSize: 12,
      lineHeight: "12px",
      padding: "2px 8px",
      height: 24,
      borderRadius: 3,
      margin: 5,
    },
  },
}));

/**
 * Save / Load user defined search filters for flights, ground events and pairings
 * @param props -
 */
export const SavedSearchFilters = (props: ISavedSearchFiltersProps): JSX.Element => {
  const { dataType, searchFilters, parentElement, setAppliedSavedFilter } = props;
  const dispatch = useDispatch();
  const fileUpload = useRef<HTMLInputElement>();

  /**
   * Check to make filters passed in from events all have an assigned value.
   */
  const searchFiltersValid = useMemo(() => {
    let searchValid = true;
    searchFilters?.forEach((filter) => {
      if (!filter.searchValue || (Array.isArray(filter.searchValue) && filter.searchValue?.length === 0)) {
        searchValid = false; // set search as invalid if any filters have no assigned value.
      }
    });
    return searchValid;
  }, [searchFilters]);

  useEffect(() => {
    const message = !searchFiltersValid ? "One of more of the assigned filters are missing values" : "";
    setErrorMessage(message);
  }, [searchFiltersValid]);

  const appSettings: IUserAppSettings | null = useSelector((state: IRootState) => state.authentication.appSettings);
  const savedSearchFilters: ISavedFilter[] = useMemo(
    () => appSettings?.savedSearchFilters ?? [],
    [appSettings?.savedSearchFilters]
  );

  // Show only saved filters that are associated with selected dataType
  const dataTypeFilters = useMemo(
    () => savedSearchFilters?.filter((filter) => filter?.dataType === dataType),
    [savedSearchFilters, dataType]
  );

  const dataTypeLabel = useMemo(() => {
    return dataType === "flights"
      ? "Flight"
      : dataType === "groundEvents"
        ? "Ground Event"
        : dataType === "pairings"
          ? "Pairing"
          : dataType === "content"
            ? "Content"
            : "";
  }, [dataType]);

  const filterCount = dataTypeFilters?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>(null);
  const [selectedSavedfilter, setSelectedSavedfilter] = useState<ISavedFilter>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // If dataType changed, reset the state values.
  useEffect(() => {
    setIsSubmitting(false);
    setFilterName(null);
    setSelectedSavedfilter(null);
    setErrorMessage("");
  }, [dataType]);

  // Only allow update if the filters or name have been modified
  const allowUpdate = useMemo(() => {
    const filtersModified = !isEqual(selectedSavedfilter?.filters ?? [], searchFilters);
    return filtersModified || selectedSavedfilter?.name !== filterName;
  }, [selectedSavedfilter, searchFilters, filterName]);

  useEffect(() => {
    setFilterName(selectedSavedfilter?.name);
    // Pass a clone of the filter so as to note mutate the original if user makes changes to the filter
    const savedfilterClone = cloneDeep(selectedSavedfilter);
    setAppliedSavedFilter(savedfilterClone); // Pass this selected saved filter to the Filter components to load them.
  }, [selectedSavedfilter, setAppliedSavedFilter]);

  const handleSaveFilters = useCallback(
    async (filters: ISavedFilter[], deleteEntry?: boolean): Promise<void> => {
      setIsSubmitting(true);

      const updatedSavedSearchFilters = [...filters];
      const index = selectedSavedfilter?._id
        ? updatedSavedSearchFilters.findIndex((filter) => filter._id === selectedSavedfilter?._id)
        : -1;
      const mode = index !== -1 && deleteEntry ? "delete" : index !== -1 && !deleteEntry ? "update" : "create";
      let updatedSavedFilter: ISavedFilter | null = null;

      // Prepare payload
      switch (mode) {
        case "create": // CREATE NEW FILTER
          updatedSavedFilter = {
            _id: `filter_${v1()}`,
            name: filterName,
            dataType: dataType,
            filters: searchFilters,
            dateCreated: new Date(),
            displayOrder: filterCount + 1,
          };
          updatedSavedSearchFilters.push(updatedSavedFilter);
          break;
        case "update": // UPDATE FILTER
          updatedSavedFilter = { ...updatedSavedSearchFilters[index], filters: searchFilters, name: filterName };
          updatedSavedSearchFilters[index] = updatedSavedFilter;
          break;
        case "delete": // DELETE FILTER
          updatedSavedSearchFilters.splice(index, 1);
      }

      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Saved Search Filters...",
        successMessage: "Search Filters Successfully Updated.",
        errorMessage: "Error: Unable to Update Saved Search Filters.",
      };

      const response = await AmplifyAPIWrapper.Put<IUserAppSettings>(
        "/me/settings",
        {
          savedSearchFilters: updatedSavedSearchFilters,
        },
        notifications
      );

      // If successfully saved, update redux with most recent values.
      if (response?.savedSearchFilters) {
        // console.log("updatedSavedFilter", updatedSavedFilter);
        setSelectedSavedfilter(updatedSavedFilter);
        dispatch(updateUserAppSettings(response, "savedSearchFilters"));
      }
      setIsSubmitting(false);
    },
    [selectedSavedfilter?._id, dispatch, filterName, dataType, searchFilters, filterCount]
  );

  const handleDownloadSavedFilters = useCallback((): void => {
    // Create JSON BLOB
    const { ...json } = selectedSavedfilter; // don't strip out _id;
    const jsonStr = JSON.stringify(json);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    // INITIATE DOWNLOAD LINK in browser.
    const savedFiltersDownload = document.createElement("a");
    savedFiltersDownload.href = href;
    savedFiltersDownload.target = "_blank";
    savedFiltersDownload.setAttribute("download", `${selectedSavedfilter.name}.json`);
    savedFiltersDownload.click();
  }, [selectedSavedfilter]);

  const handleUploadSavedFilters = useCallback(
    (savedFilter: any): void => {
      if (!savedFilter?.name || savedFilter?.size > 3000 || savedFilter?.type !== "application/json") {
        setErrorMessage("Uploaded file is too large or of incorrect type.");
        return;
      }
      const fileReader = new FileReader();
      fileReader.readAsText(savedFilter, "UTF-8");
      fileReader.onload = (e) => {
        if (e?.target?.result && typeof e.target.result === "string") {
          try {
            const jsonFilter: ISavedFilter = JSON.parse(e.target.result);

            if (!jsonFilter?.dataType) {
              setErrorMessage("Data type for uploaded filter unavailable");
              return;
            }

            if (jsonFilter?.dataType !== dataType) {
              setErrorMessage(
                `Data type for uploaded filter "${jsonFilter?.dataType}" does not match currently selected event category "${dataType}".`
              );
              return;
            }

            const idIndex = savedSearchFilters?.findIndex((item) => item?._id === jsonFilter?._id);
            const nameIndex = savedSearchFilters?.findIndex((item) => item?.name === jsonFilter?.name);

            if (jsonFilter?.name && nameIndex !== -1) {
              setErrorMessage("An item with this name was already found in your saved filters.");
            } else {
              const updatedFilters = [...savedSearchFilters];
              if (idIndex !== -1) {
                updatedFilters[idIndex] = jsonFilter; // Update filter with matching _id.
              } else {
                updatedFilters.push(jsonFilter); // add new filter
              }
              setSelectedSavedfilter(jsonFilter);
              handleSaveFilters(updatedFilters);
            }
          } catch (e) {
            setErrorMessage("Unable to read imported file filter.");
          }
        }
      };
      // Clean out the input component.
      if (fileUpload?.current?.value) fileUpload.current.value = null;
    },
    [handleSaveFilters, savedSearchFilters, dataType]
  );

  const disableDownload = !selectedSavedfilter?._id || isSubmitting || !searchFiltersValid || !!allowUpdate;
  const disableUpload = !!selectedSavedfilter;

  return (
    <OCSavedSearchFilters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {`Saved ${dataTypeLabel} Search Filters`}
        <span className="form_subtitle">{dataTypeFilters?.length > 0 ? `(${dataTypeFilters?.length})` : ""}</span>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container alignItems="center">
          <Grid container wrap="nowrap" alignItems="center" className="saved_search_selection">
            <div className="saved_search_icon">
              <SearchIcon className="search_icon" />
              <StarIcon className="star_icon" />
            </div>
            <Grid container style={{ width: "auto" }} alignItems="center">
              <SelectField
                className="timeline_event_saved_search_filter"
                label=""
                name="advanced_saved_search_filters"
                value={selectedSavedfilter}
                getOptionValue={(option: ISavedFilter) => option.name}
                getOptionLabel={(option: ISavedFilter) => option.name}
                options={dataTypeFilters}
                onChange={(option: ISavedFilter) => {
                  setSelectedSavedfilter(option);
                }}
                disabled={savedSearchFilters?.length === 0}
                isMulti={false}
                isClearable={true}
                menuPortalTarget={parentElement}
                menuPosition="fixed"
                maxMenuHeight={400}
                placeholder="Saved Search Filters"
              />
              <IconButton
                tooltipProps={{
                  title: `${
                    disableDownload
                      ? "Only un-modified saved filters can be downloaded."
                      : `Download "${selectedSavedfilter?.name}" search filter as file.`
                  }`,
                  enterDelay: 500,
                  placement: "top",
                }}
                style={{ padding: 0, borderRadius: 5 }}
                className="download_saved_filters_btn"
                color="primary"
                onClick={() => handleDownloadSavedFilters()}
                disabled={disableDownload}
                size="large"
              >
                <DownloadIcon />
              </IconButton>

              <IconButton
                tooltipProps={{
                  title: `${
                    disableUpload ? "Clear selection to upload new filter." : "Upload search filter json file."
                  }`,
                  enterDelay: 500,
                  placement: "top",
                }}
                style={{ padding: 0, borderRadius: 5 }}
                className="upload_saved_filters_btn"
                color="primary"
                onClick={() => {
                  setErrorMessage("");
                  fileUpload?.current?.click();
                }}
                disabled={disableUpload}
                size="large"
              >
                <UploadIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item style={{ width: 400 }}>
            <TextField
              className="field textfield"
              label="Filter Name"
              name="savedFilterName"
              value={filterName}
              disabled={searchFilters?.length === 0}
              onChange={(event: any) => {
                if (event?.target?.value) {
                  setFilterName(event?.target?.value);
                } else {
                  setFilterName(selectedSavedfilter?.name ?? null); // if cleared, reset to current saved name if available
                }
              }}
            />
          </Grid>

          <Grid container className="actions">
            {selectedSavedfilter?._id && (
              <Button
                className={"button saved_filter_create_btn"}
                variant="contained"
                color="secondary"
                onClick={() => {
                  const savedfilterClone = cloneDeep(selectedSavedfilter);
                  setAppliedSavedFilter(savedfilterClone);
                }}
                disabled={isEqual(searchFilters, selectedSavedfilter?.filters)}
              >
                Reset
              </Button>
            )}
            <Button
              className={"button saved_filter_create_btn"}
              variant="contained"
              color="primary"
              onClick={() => handleSaveFilters(savedSearchFilters)}
              disabled={
                searchFilters?.length < 1 ||
                !filterName ||
                isSubmitting ||
                (!allowUpdate && !!selectedSavedfilter?._id) ||
                !searchFiltersValid
              }
            >
              {selectedSavedfilter?._id ? "Update" : "Save New"}
            </Button>
            {selectedSavedfilter?._id && (
              <Button
                className="button saved_filter_delete_btn"
                variant="contained"
                color="primary"
                onClick={() => handleSaveFilters(savedSearchFilters, true)}
                disabled={!selectedSavedfilter?._id || isSubmitting}
              >
                Delete
              </Button>
            )}
          </Grid>

          <input
            ref={fileUpload}
            onChange={(event: any) => {
              handleUploadSavedFilters(event.target.files[0]);
            }}
            type="file"
            style={{ display: "none" }}
            multiple={false}
          />

          <Typography color="error" variant="caption" style={{ marginLeft: 10 }}>
            {errorMessage}
          </Typography>
        </Grid>
      </AccordionDetails>
    </OCSavedSearchFilters>
  );
};
